import { c as _c } from "react/compiler-runtime";
import DropsList from "@/components/drops/view/DropsList";
import { WaveDropsReverseContainer } from "@/components/waves/drops/WaveDropsReverseContainer";
import { WaveDropsScrollBottomButton } from "@/components/waves/drops/WaveDropsScrollBottomButton";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { RefObject } from "react";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsMessageListSectionProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
  readonly bottomAnchorRef: RefObject<HTMLDivElement | null>;
  readonly onTopIntersection: () => void;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly onQuote: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: RefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
  readonly bottomPaddingClassName?: string | undefined;
  readonly unreadDividerSerialNo?: number | null | undefined;
  readonly boostedDrops?: ApiDrop[] | undefined;
  readonly onBoostedDropClick?: ((serialNo: number) => void) | undefined;
}
const MIN_DROPS_FOR_PAGINATION = 25;
export const WaveDropsMessageListSection: React.FC<WaveDropsMessageListSectionProps> = t0 => {
  const $ = _c(36);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    bottomAnchorRef,
    onTopIntersection,
    onReply,
    onQuote,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    onDropContentClick,
    pendingCount,
    onRevealPending,
    bottomPaddingClassName,
    unreadDividerSerialNo,
    boostedDrops,
    onBoostedDropClick
  } = t0;
  const hasNextPage = !!waveMessages?.hasNextPage && waveMessages.drops.length >= MIN_DROPS_FOR_PAGINATION;
  const t1 = !!waveMessages?.isLoadingNextPage;
  let t2;
  if ($[0] !== waveMessages?.drops) {
    t2 = waveMessages?.drops ?? [];
    $[0] = waveMessages?.drops;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== activeDrop || $[3] !== boostedDrops || $[4] !== dropId || $[5] !== onBoostedDropClick || $[6] !== onDropContentClick || $[7] !== onQuote || $[8] !== onQuoteClick || $[9] !== onReply || $[10] !== queueSerialTarget || $[11] !== scrollContainerRef || $[12] !== serialTarget || $[13] !== t2 || $[14] !== targetDropRef || $[15] !== unreadDividerSerialNo) {
    t3 = <DropsList scrollContainerRef={scrollContainerRef} onReplyClick={queueSerialTarget} drops={t2} showWaveInfo={false} onReply={onReply} onQuote={onQuote} showReplyAndQuote={true} activeDrop={activeDrop} serialNo={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} parentContainerRef={scrollContainerRef} dropViewDropId={dropId} onDropContentClick={onDropContentClick} unreadDividerSerialNo={unreadDividerSerialNo} boostedDrops={boostedDrops} onBoostedDropClick={onBoostedDropClick} key="drops-list" />;
    $[2] = activeDrop;
    $[3] = boostedDrops;
    $[4] = dropId;
    $[5] = onBoostedDropClick;
    $[6] = onDropContentClick;
    $[7] = onQuote;
    $[8] = onQuoteClick;
    $[9] = onReply;
    $[10] = queueSerialTarget;
    $[11] = scrollContainerRef;
    $[12] = serialTarget;
    $[13] = t2;
    $[14] = targetDropRef;
    $[15] = unreadDividerSerialNo;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      height: "1px"
    };
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== bottomAnchorRef) {
    t5 = <div ref={bottomAnchorRef} style={t4} />;
    $[18] = bottomAnchorRef;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== bottomPaddingClassName || $[21] !== hasNextPage || $[22] !== onTopIntersection || $[23] !== scrollContainerRef || $[24] !== t1 || $[25] !== t3 || $[26] !== t5) {
    t6 = <WaveDropsReverseContainer ref={scrollContainerRef} isFetchingNextPage={t1} hasNextPage={hasNextPage} onTopIntersection={onTopIntersection} bottomPaddingClassName={bottomPaddingClassName}>{t3}{t5}</WaveDropsReverseContainer>;
    $[20] = bottomPaddingClassName;
    $[21] = hasNextPage;
    $[22] = onTopIntersection;
    $[23] = scrollContainerRef;
    $[24] = t1;
    $[25] = t3;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== isAtBottom || $[29] !== onRevealPending || $[30] !== pendingCount || $[31] !== scrollToBottom) {
    t7 = <WaveDropsScrollBottomButton isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} newMessagesCount={pendingCount} onRevealNewMessages={onRevealPending} />;
    $[28] = isAtBottom;
    $[29] = onRevealPending;
    $[30] = pendingCount;
    $[31] = scrollToBottom;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t6 || $[34] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
};